import max from 'lodash-es/max'

import { EMPTY_LIST } from '../../../../constants/common'
import {
  getCalendarGridItemDuration,
  getCalendarGridItemsSortedByStart,
  getCalendarGridMinuteOffset,
  getIsCalendarGridToday,
} from '../../CalendarGrid.utils'
import { CALENDAR_GRID_WEEK_WINDOW_MINUTES } from '../CalendarGridWeek.constants'
import { getIsCalendarGridTimeInWindow } from '../CalendarGridWeek.utils'

import {
  CALENDAR_GRID_WEEK_GROUP_MORE_KEY_SUFFIX,
  CALENDAR_GRID_WEEK_LANE_COUNT_WITH_MORE,
  CALENDAR_GRID_WEEK_LANE_LIMIT,
  CALENDAR_GRID_WEEK_NO_FREE_LANE,
  CALENDAR_GRID_WEEK_TIME_DROP_AREA_PREFIX,
} from './CalendarGridWeekColumn.constants'

function getCalendarGridFreeLane({ laneEnds, startOffset }) {
  const freeLane = laneEnds.findIndex(laneEnd => laneEnd <= startOffset)

  return freeLane === CALENDAR_GRID_WEEK_NO_FREE_LANE
    ? laneEnds.length
    : freeLane
}

function getCalendarGridGroupLaneEntries({
  firstHour,
  items,
  locale,
  timezone,
}) {
  return items.reduce(
    ({ entries, laneEnds }, item) => {
      const { endOffset, startOffset } = getCalendarGridItemOffsets({
        firstHour,
        item,
        locale,
        timezone,
      })

      const lane = getCalendarGridFreeLane({ laneEnds, startOffset })

      return {
        entries: [
          ...entries,
          {
            end: item.end,
            endOffset,
            items: [item],
            lane,
            start: item.start,
            startOffset,
          },
        ],
        laneEnds: [
          ...laneEnds.slice(0, lane),
          endOffset,
          ...laneEnds.slice(lane + 1),
        ],
      }
    },
    { entries: EMPTY_LIST, laneEnds: EMPTY_LIST }
  ).entries
}

function getCalendarGridDayGroups({ firstHour, items, locale, timezone }) {
  return getCalendarGridItemsSortedByStart(items).reduce((groups, item) => {
    const { endOffset, startOffset } = getCalendarGridItemOffsets({
      firstHour,
      item,
      locale,
      timezone,
    })

    const group = groups[groups.length - 1]

    return group && startOffset < group.endOffset
      ? [
          ...groups.slice(0, -1),
          {
            end: max([group.end, item.end]),
            endOffset: Math.max(group.endOffset, endOffset),
            items: [...group.items, item],
            start: group.start,
            startOffset: group.startOffset,
          },
        ]
      : [
          ...groups,
          {
            end: item.end,
            endOffset,
            items: [item],
            start: item.start,
            startOffset,
          },
        ]
  }, EMPTY_LIST)
}

function getCalendarGridGroupBlocks({ firstHour, group, locale, timezone }) {
  const entries = getCalendarGridGroupLaneEntries({
    firstHour,
    items: group.items,
    locale,
    timezone,
  })

  const laneCount = Math.max(...entries.map(entry => entry.lane)) + 1

  const drawnEntries = entries
    .filter(entry => entry.lane < CALENDAR_GRID_WEEK_LANE_LIMIT)
    .map(entry => ({
      ...entry,
      laneCount: CALENDAR_GRID_WEEK_LANE_COUNT_WITH_MORE,
    }))

  return laneCount > CALENDAR_GRID_WEEK_LANE_LIMIT
    ? [
        ...drawnEntries,
        {
          ...group,
          lane: CALENDAR_GRID_WEEK_LANE_LIMIT,
          laneCount: CALENDAR_GRID_WEEK_LANE_COUNT_WITH_MORE,
          moreCount: group.items.length - drawnEntries.length,
        },
      ]
    : entries.map(entry => ({ ...entry, laneCount }))
}

export function getCalendarGridItemOffsets({
  firstHour,
  item,
  locale,
  timezone,
}) {
  const startOffset = getCalendarGridMinuteOffset({
    firstHour,
    locale,
    timezone,
    value: item.start,
  })

  return {
    endOffset: Math.min(
      startOffset + getCalendarGridItemDuration(item),
      CALENDAR_GRID_WEEK_WINDOW_MINUTES
    ),
    startOffset,
  }
}

export function getCalendarGridDayBlocks({
  firstHour,
  items,
  locale,
  timezone,
}) {
  return getCalendarGridDayGroups({
    firstHour,
    items,
    locale,
    timezone,
  }).flatMap(group =>
    getCalendarGridGroupBlocks({ firstHour, group, locale, timezone })
  )
}

export function getCalendarGridTimeDropAreaId(day) {
  return `${CALENDAR_GRID_WEEK_TIME_DROP_AREA_PREFIX}${day}`
}

export function getIsCalendarGridNowLineShown({
  day,
  firstHour,
  locale,
  now,
  timezone,
  today,
}) {
  return (
    getIsCalendarGridToday({ day, today }) &&
    getIsCalendarGridTimeInWindow({ firstHour, locale, timezone, value: now })
  )
}

export function getIsCalendarGridWeekLastHour({ hour, hours }) {
  return hour.value === hours[hours.length - 1].value
}

export function getIsCalendarGridPreviewOnDay({ day, previewItem }) {
  return previewItem?.date === day.date
}

export function getCalendarGridWeekGroupKey({ items: [item], moreCount }) {
  return moreCount
    ? `${item.id}${CALENDAR_GRID_WEEK_GROUP_MORE_KEY_SUFFIX}`
    : item.id
}
